import { Upload, defaultOptions } from "tus-js-client";
import { apiFetch } from "@/lib/api-client";
import { refreshAuthSession } from "@/lib/auth/refresh-client";
import { ATTACH_CHUNK_BYTES, ATTACH_RESUMABLE_PATH } from "@/lib/upload/resumable-constants";
import { queueAttachUpload } from "@/lib/upload/attach-upload-queue";
import type { AttachFileRecord } from "@/components/admin/attach-picker/types";
import type { UploadKind } from "@/lib/upload/kinds";

export type AttachUploadTaskStatus =
  | "uploading"
  | "success"
  | "error"
  | "cancelled";

export type AttachUploadTask = {
  id: string;
  file: File;
  previewUrl: string;
  progress: number;
  status: AttachUploadTaskStatus;
  error?: string;
  phase?: "uploading" | "saving";
};

export type AttachUploadPayload = {
  file: File;
  replaceId?: string;
  kind: UploadKind;
  groupId?: string;
  module?: string;
  visibility?: string;
  onProgress?: (percent: number) => void;
  onPhase?: (phase: "uploading" | "saving") => void;
  signal?: AbortSignal;

  messages?: { failed: string; network: string };
};

function createUploadTaskId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return `upload-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function createAttachUploadTask(file: File): AttachUploadTask {
  return {
    id: createUploadTaskId(),
    file,
    previewUrl: URL.createObjectURL(file),
    progress: 0,
    status: "uploading",
  };
}

export function revokeAttachUploadTaskPreview(task: AttachUploadTask) {
  URL.revokeObjectURL(task.previewUrl);
}

async function sendAttach(payload: AttachUploadPayload): Promise<AttachFileRecord> {
  const { actorId } = await apiFetch<{ actorId: string }>(`${ATTACH_RESUMABLE_PATH}/context`, { signal: payload.signal });
  if (payload.signal?.aborted) throw new DOMException("Upload aborted", "AbortError");
  const metadata = { filename: payload.file.name, filetype: payload.file.type, kind: payload.kind,
    groupId: payload.groupId ?? "default", module: payload.module ?? "profile", visibility: payload.visibility ?? "",
    replaceId: payload.replaceId ?? "" };
  return new Promise<AttachFileRecord>((resolve, reject) => {
    let refreshed = false;
    let settled = false;
    function finish(error?: Error, attach?: AttachFileRecord) {
      if (settled) return;
      settled = true;
      payload.signal?.removeEventListener("abort", abort);
      if (error) reject(error); else resolve(attach!);
    }
    function abort() {
      void upload.abort(payload.signal?.reason === "discard").catch(() => {});
      finish(new DOMException("Upload aborted", "AbortError"));
    }
    const upload = new Upload(payload.file, {
      endpoint: ATTACH_RESUMABLE_PATH,
      chunkSize: ATTACH_CHUNK_BYTES, parallelUploads: 1,
      retryDelays: [0, 1000, 3000, 5000, 10000],
      metadata, removeFingerprintOnSuccess: false,
      fingerprint: async (file, options) => `${actorId}:${JSON.stringify(metadata)}:${await defaultOptions.fingerprint(file, options)}`,
      onBeforeRequest(request) {
        const url = new URL(request.getURL(), window.location.origin);
        if (url.origin !== window.location.origin || !url.pathname.startsWith(`${ATTACH_RESUMABLE_PATH}`)) throw new Error("上传地址无效");
        const xhr = request.getUnderlyingObject();
        if (xhr instanceof XMLHttpRequest) xhr.withCredentials = true;
      },
      async onAfterResponse(_request, response) {
        const status = response.getStatus();
        if (status === 401 && !refreshed) refreshed = (await refreshAuthSession()).ok;
        else if (status >= 200 && status < 300) refreshed = false;
      },
      onShouldRetry(error) {
        const status = error.originalResponse?.getStatus() ?? 0;
        return !payload.signal?.aborted && (status === 0 || status >= 500 || status === 409 || status === 423 || (status === 401 && refreshed));
      },
      onProgress(sent, total) { payload.onProgress?.(total ? Math.min(99, Math.round(sent / total * 100)) : 0); },
      onError(error) {
        const response = "originalResponse" in error ? error.originalResponse : null;
        const body = response?.getBody();
        let message = body;
        try { message = JSON.parse(body ?? "{}").error?.message ?? body; } catch { /* tus 原生错误为纯文本。 */ }
        finish(new Error(message || payload.messages?.failed || "上传失败，请稍后重试"));
      },
      onSuccess() {
        void (async () => {
          if (!upload.url || settled) return;
          const url = new URL(upload.url, window.location.origin);
          payload.onPhase?.("saving");
          let result = await apiFetch<{ attach?: AttachFileRecord; pending?: boolean; progress?: number }>(`${url.pathname}/complete`, { method: "POST", signal: payload.signal });
          while (result.pending && !payload.signal?.aborted) {

            payload.onProgress?.(Math.min(99, result.progress ?? 0));
            await new Promise<void>((resolve, reject) => {
              const abort = () => { clearTimeout(timer); reject(new DOMException("Upload aborted", "AbortError")); };
              const timer = setTimeout(() => { payload.signal?.removeEventListener("abort", abort); resolve(); }, 1500);
              payload.signal?.addEventListener("abort", abort, { once: true });
            });
            result = await apiFetch(`${url.pathname}/complete`, { signal: payload.signal });
          }
          if (!result.attach) throw new Error("附件保存尚未完成，请重试");

          const previous = await upload.findPreviousUploads().catch(() => []);
          await Promise.all(previous.filter((item) => item.uploadUrl === upload.url).map((item) => upload.options.urlStorage?.removeUpload(item.urlStorageKey))).catch(() => {});
          payload.onProgress?.(100);
          finish(undefined, result.attach);
        })().catch((error: Error) => finish(error));
      },
    });
    payload.signal?.addEventListener("abort", abort, { once: true });
    void upload.findPreviousUploads().then((previous) => {
      if (settled) return;
      const resumable = previous.find((item) => {
        if (!item.uploadUrl) return false;
        const url = new URL(item.uploadUrl, window.location.origin);
        return url.origin === window.location.origin && new RegExp(`^${ATTACH_RESUMABLE_PATH}/[a-f0-9]{32}$`).test(url.pathname);
      });
      if (resumable) upload.resumeFromPreviousUpload(resumable);
      upload.start();
    }).catch((error: Error) => finish(error));
  });
}

export function uploadAttachWithProgress(payload: AttachUploadPayload): Promise<AttachFileRecord> {
  return queueAttachUpload(async () => {
    payload.onPhase?.("uploading");
    const { tryDirectAttachUpload } = await import("./attach-direct-client");
    return await tryDirectAttachUpload(payload) ?? sendAttach(payload);
  }, payload.signal);
}
